import { useEffect, useState } from 'react'
import { useTranslation } from 'next-i18next'
import { useRouter } from 'next/router'

import usePricePagePromotion from '~/get-started/us-onboarding/hooks/usePricePagePromotion'
import LandingCtaButton from '~/landingV4/components/LandingCtaButton'
import PricingPlanOption from '~/landingV4/components/PricingPlanOption'
import SectionContainer from '~/landingV4/components/SectionContainer'
import useSubscriptionPackageFormatter from '~/subscription/hooks/useSubscriptionPackagesFormatter'
import useSubscriptionToggle from '~/subscription/hooks/useSubscriptionToggle'
import { AppLanguage } from '~/types/locale'
import { DAYS_PER_MONTH, DAYS_PER_YEAR, PRICING_PLANS } from '~/landingV4/consts/pricingPlans.const'
import { MAX_LICENSES_WITHOUT_ADMIN } from '~/familyAccount/familyAccounts.constants'
import useMinimumFamilyLicenses from '~/familyAccount/hooks/useMinimumFamilyLicenses'
import { PricingPackageType } from '~/landingV4/types/pricing.types'
import { formatToLocalCurrency } from '~/subscription/util'

const CHECK = '✓'

export default function PricingSection(): React.ReactElement {
  const { t } = useTranslation('abTestLanding')
  const router = useRouter()
  const language = router.locale as AppLanguage
  const { updateSubscriptionPackageType } = useSubscriptionToggle()
  const [selectedPackage, setSelectedPackage] = useState<PricingPackageType>('yearly')
  const minLicenses = useMinimumFamilyLicenses()
  const [numberOfLicenses, setNumberOfLicenses] = useState(1)

  // Start an existing family admin on the licences they already have, and never go below them
  useEffect(() => {
    setNumberOfLicenses((prev) => (prev < minLicenses ? minLicenses : prev))
  }, [minLicenses])

  const { packages } = useSubscriptionPackageFormatter('packageType')

  useEffect(() => {
    updateSubscriptionPackageType('family')
  }, [updateSubscriptionPackageType])

  const familyPackages = packages?.filter((pkg) => pkg.isFamilyPrice) || []
  const yearlyPackage = familyPackages.find((pkg) => pkg.stripePaymentInterval?.toLowerCase().includes('year'))
  const monthlyPackage = familyPackages.find((pkg) => pkg.stripePaymentInterval?.toLowerCase().includes('month'))
  const currency = packages[0]?.currency || 'USD'

  const additionalLicenses = numberOfLicenses - 1
  const yearlyBase = yearlyPackage?.price ?? 0
  const monthlyBase = monthlyPackage?.price ?? 0
  const yearlyLicense = yearlyPackage?.licensePrice ?? 0
  const monthlyLicense = monthlyPackage?.licensePrice ?? 0
  const yearlyTotal = yearlyBase + yearlyLicense * additionalLicenses
  const monthlyTotal = monthlyBase + monthlyLicense * additionalLicenses

  const savePercent = monthlyBase > 0 && yearlyBase > 0 ? Math.floor((1 - yearlyBase / (monthlyBase * 12)) * 100) : 0

  // Back-to-school promo, mirroring the get-started price page (usePricePagePromotion).
  // Each interval carries its own promo so both cards can show their discount at the same time.
  const yearlyPromo = usePricePagePromotion({ currency, interval: 'yearly', priceId: yearlyPackage?.priceId })
  const monthlyPromo = usePricePagePromotion({ currency, interval: 'monthly', priceId: monthlyPackage?.priceId })
  const promoByType: Record<PricingPackageType, ReturnType<typeof usePricePagePromotion>> = {
    yearly: yearlyPromo,
    monthly: monthlyPromo,
  }
  const yearlySaveBadge = yearlyPromo.promoPercentage ?? savePercent
  const monthlySaveBadge = monthlyPromo.promoPercentage ?? savePercent

  const label = (amount: number): string => formatToLocalCurrency(currency, amount, language).priceLabel

  const totalByType: Record<PricingPackageType, number> = { yearly: yearlyTotal, monthly: monthlyTotal }
  const perDayByType: Record<PricingPackageType, number> = {
    yearly: yearlyTotal / DAYS_PER_YEAR,
    monthly: monthlyTotal / DAYS_PER_MONTH,
  }
  const daysByType: Record<PricingPackageType, number> = { yearly: DAYS_PER_YEAR, monthly: DAYS_PER_MONTH }
  const discountedTotalByType: Record<PricingPackageType, number | undefined> = {
    yearly: yearlyPromo.isPromoActive ? yearlyPromo.applyPromoDiscount(yearlyTotal) : undefined,
    monthly: monthlyPromo.isPromoActive ? monthlyPromo.applyPromoDiscount(monthlyTotal) : undefined,
  }

  const selectedDiscountedTotal = discountedTotalByType[selectedPackage]
  const selectedCoupon = promoByType[selectedPackage].coupon
  const selectedDiscountAmount =
    selectedDiscountedTotal !== undefined ? totalByType[selectedPackage] - selectedDiscountedTotal : 0

  const onLicenseChange = (type: '+' | '-'): void => {
    setNumberOfLicenses((prev) =>
      type === '+' ? Math.min(MAX_LICENSES_WITHOUT_ADMIN, prev + 1) : Math.max(minLicenses, prev - 1),
    )
  }

  const features = [t('v4.pricing.includingTax'), t('v4.pricing.noHiddenFees'), t('v4.pricing.noExtraPayment')]

  return (
    <SectionContainer className="flex flex-col items-center gap-8 py-15 md:gap-10 md:py-20">
      <div className="flex max-w-128 flex-col items-center gap-4 text-center">
        <h2 className="text-h4 font-extrabold leading-tight tracking-tight text-black-ink md:text-h2">
          {t('v4.pricing.heading')}
        </h2>
        <p className="text-5 text-purple-muted-violet">{t('v4.pricing.subheading')}</p>
      </div>

      <div className="flex w-full max-w-147 flex-col overflow-hidden rounded-24 border border-green-lime bg-purple-card text-left shadow-black">
        <div className="flex flex-col gap-3 px-4 md:px-6 py-5">
          <div className="flex items-center justify-between">
            <div className="flex flex-col gap-1">
              <p className="text-md font-bold text-white">{t('v4.pricing.learners')}</p>
              <p className="text-xs text-purple-haze/60">
                {t('v4.pricing.addLearners', { max: MAX_LICENSES_WITHOUT_ADMIN })}
              </p>
            </div>
            <div className="flex items-center gap-3">
              <button
                aria-label={t('v4.pricing.removeLearner')}
                className={`flex size-7 items-center justify-center rounded-2.5 bg-gray-50 text-md font-bold text-purple-darker ${
                  numberOfLicenses <= minLicenses ? 'opacity-40' : ''
                }`}
                onClick={() => onLicenseChange('-')}
                type="button"
              >
                &minus;
              </button>
              <span className="flex h-9 w-11 items-center justify-center rounded-2 bg-white text-md font-bold text-purple-vivid">
                {numberOfLicenses}
              </span>
              <button
                aria-label={t('v4.pricing.addLearner')}
                className="flex size-7 items-center justify-center rounded-2.5 bg-purple-vivid text-md font-bold text-white"
                onClick={() => onLicenseChange('+')}
                type="button"
              >
                +
              </button>
            </div>
          </div>
          <span className="block h-px w-full bg-white/10" />
          <div className="flex flex-col gap-1.5 text-sm text-purple-haze">
            <div className="flex items-center justify-between gap-2">
              <p className="min-w-0">{t('v4.pricing.firstLearner')}</p>
              <p className="shrink-0 whitespace-nowrap">
                {label(selectedPackage === 'yearly' ? yearlyBase : monthlyBase)}
              </p>
            </div>
            <div className="flex items-center justify-between gap-2">
              <p className="min-w-0">{t('v4.pricing.additionalLearners', { count: additionalLicenses })}</p>
              <p className="shrink-0 whitespace-nowrap">
                {label((selectedPackage === 'yearly' ? yearlyLicense : monthlyLicense) * additionalLicenses)}
              </p>
            </div>
            {selectedDiscountedTotal !== undefined && (
              <div className="flex items-start justify-between gap-2 text-green-mint-bright">
                <p className="min-w-0">
                  {t('v4.pricing.discount')}
                  {selectedCoupon?.name ? ` (${selectedCoupon.name})` : ''}
                </p>
                <p className="shrink-0 whitespace-nowrap">&minus;{label(selectedDiscountAmount)}</p>
              </div>
            )}
          </div>
          <span className="block h-px w-full bg-white/10" />
          <div className="flex items-baseline justify-between gap-2 text-sm font-bold text-white">
            <p className="shrink-0">{t('v4.pricing.total')}</p>
            <p className="min-w-0 text-right">
              {selectedDiscountedTotal !== undefined ? (
                <>
                  <span className="font-normal text-purple-haze/50 line-through">
                    {label(totalByType[selectedPackage])}
                  </span>{' '}
                  {t(selectedPackage === 'yearly' ? 'v4.pricing.billedAnnually' : 'v4.pricing.billedMonthly', {
                    price: label(selectedDiscountedTotal),
                  })}
                </>
              ) : (
                t(selectedPackage === 'yearly' ? 'v4.pricing.billedAnnually' : 'v4.pricing.billedMonthly', {
                  price: label(totalByType[selectedPackage]),
                })
              )}
            </p>
          </div>
        </div>

        <div className="flex flex-col gap-4 px-4 md:px-6 pt-4">
          {PRICING_PLANS.map((plan) => {
            const discountedTotal = discountedTotalByType[plan.type]
            const displayTotal = discountedTotal ?? totalByType[plan.type]
            const displayPerDay =
              discountedTotal !== undefined ? discountedTotal / daysByType[plan.type] : perDayByType[plan.type]
            return (
              <PricingPlanOption
                isPopular={plan.isPopular}
                isSelected={selectedPackage === plan.type}
                key={plan.type}
                onSelect={() => setSelectedPackage(plan.type)}
                originalPerDayPrice={discountedTotal !== undefined ? label(perDayByType[plan.type]) : undefined}
                originalSubPrice={discountedTotal !== undefined ? label(totalByType[plan.type]) : undefined}
                perDayLabel={t('v4.pricing.perDay')}
                perDayPrice={label(displayPerDay)}
                popularLabel={t('v4.pricing.mostPopular')}
                saveLabel={
                  plan.hasSaveBadge
                    ? t('v4.pricing.save', { percent: plan.type === 'yearly' ? yearlySaveBadge : monthlySaveBadge })
                    : undefined
                }
                subPrice={label(displayTotal)}
                title={t(plan.titleKey)}
              />
            )
          })}
        </div>

        <div className="flex flex-col-reverse items-center gap-4 px-6 pb-7 pt-6 md:flex-col">
          <div className="flex flex-col items-center justify-center gap-1.5 text-3.25 font-semibold text-green-mint-bright md:flex-row md:gap-5">
            {features.map((feature) => (
              <p key={feature}>
                {CHECK} {feature}
              </p>
            ))}
          </div>
          <LandingCtaButton analyticsLabel="landing_v4_pricing_cta" uppercase>
            {t('v4.pricing.cta')}
          </LandingCtaButton>
        </div>
      </div>
    </SectionContainer>
  )
}
